/**
 * The rule after the first walk: **no screen is ever blank**. A screen that throws while it
 * renders takes its own body down and nothing else — the shell, the rail, the phase strip and
 * the top bar stay where they are, and the body says one sentence in our words with a way on.
 * The reason goes to the festival's own log (`POST /observe/client-error`), so a screen that
 * failed for somebody is never only a cream rectangle nobody can describe.
 *
 * React gives one way to catch a render that throws — a class with `componentDidCatch` — so this
 * is the one class component in the app.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@cider/ui';
import { api } from './api.js';

export const SCREEN_ERROR_SAYS =
  'This screen hit a problem. It has been written to the log; try again, or open another screen.';

interface Props {
  /** The address of the screen, so the log says which one and a new address resets the boundary. */
  screen: string;
  children: ReactNode;
}
interface State {
  failed: boolean;
}

export class ScreenError extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidUpdate(previous: Props): void {
    // A different screen is a fresh start: the last one's failure is not this one's.
    if (previous.screen !== this.props.screen && this.state.failed)
      this.setState({ failed: false });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const body = {
      screen: this.props.screen,
      message: error.message.slice(0, 500),
      stack: `${(error.stack ?? '').slice(0, 1200)}\n${(info.componentStack ?? '').slice(0, 700)}`,
    };
    // The log is the point; a log that cannot be written must not take the screen with it.
    api('POST', '/observe/client-error', body).catch(() => undefined);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="page__body" role="alert">
        <p>{SCREEN_ERROR_SAYS}</p>
        <p>
          <Button
            kind="primary"
            onClick={() => {
              this.setState({ failed: false });
            }}
          >
            Try again
          </Button>
        </p>
      </div>
    );
  }
}
